// The room code in the top bar, as a button. A tap drops a small panel under
// the bar with the room's link and a key that copies it, so a llama can pass
// the link to another. A second tap on the code, or a tap anywhere else,
// closes the panel.

import { KEY_INK, keycap } from './chrome';
import { buzz, el } from './dom';
import { pixelText } from './pixelText';
import { play } from './sfx';
import { STRINGS } from './strings';

const CODE_SCALE = 2;
const LINK_SCALE = 2;
const CODE_INK = '#ccc8e0';
const LINK_INK = '#908ca4';
const COPY_BUZZ_MS = 20;
// How long "copied" shows before the panel closes.
const COPIED_SHOW_MS = 1200;

let openPanel: HTMLElement | null = null;

function closePanel(): void {
  openPanel?.remove();
  openPanel = null;
}

document.addEventListener('pointerdown', (event) => {
  if (!openPanel) return;
  const target = event.target as Node;
  if (openPanel.contains(target) || (target instanceof Element && target.closest('.osd-code')))
    return;
  closePanel();
});

// The link without its scheme: the words a llama would say aloud.
const shownLink = (url: string): string => url.replace(/^https?:\/\//, '');

function linkPanel(code: string): HTMLElement {
  const url = `${location.origin}/${code}`;
  const panel = el('div', 'link-panel');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', STRINGS.roomLink);
  const copy = keycap({
    label: STRINGS.copyLink,
    content: pixelText(STRINGS.copyLink.toLowerCase(), 'monogram', LINK_SCALE, KEY_INK.light),
    className: 'key-small key-copy',
    onPress: () => {
      void navigator.clipboard.writeText(url).then(() => {
        play('ding');
        buzz(COPY_BUZZ_MS);
        copy
          .querySelector('.keycap-face')
          ?.replaceChildren(
            pixelText(STRINGS.linkCopied.toLowerCase(), 'monogram', LINK_SCALE, KEY_INK.light),
          );
        setTimeout(() => {
          if (openPanel === panel) closePanel();
        }, COPIED_SHOW_MS);
      });
    },
  });
  panel.append(pixelText(shownLink(url), 'monogram', LINK_SCALE, LINK_INK, 'link-url'), copy);
  return panel;
}

export function roomCodeButton(code: string, screen: HTMLElement): HTMLButtonElement {
  const key = el('button', 'osd-code') as HTMLButtonElement;
  key.type = 'button';
  key.setAttribute('aria-label', STRINGS.roomLink);
  key.append(el('span', 'room-swatch'), pixelText(code, 'compass', CODE_SCALE, CODE_INK));
  key.addEventListener('click', () => {
    // A redraw of the glass drops the panel, so only one still shown is open.
    if (openPanel?.isConnected) {
      closePanel();
      return;
    }
    play('click');
    openPanel = linkPanel(code);
    screen.append(openPanel);
  });
  return key;
}
